import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  alpha,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import PersonIcon from '@mui/icons-material/Person'
import EventIcon from '@mui/icons-material/Event'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PersonPinIcon from '@mui/icons-material/PersonPin'
import BusinessIcon from '@mui/icons-material/Business'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PaymentIcon from '@mui/icons-material/Payment'
import ReceiptIcon from '@mui/icons-material/Receipt'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'
import TimerIcon from '@mui/icons-material/Timer'
import NotesIcon from '@mui/icons-material/Notes'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'
import { getSPDataWithParams, putSPDataWithParams } from 'src/supportPortal/services/services'
import { isCommonResponseSuccessful, getDataMapFromCommonResponse, isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { displayNamesOfEventTypes, allAvailableEventTypes, MOMENT_FORMATS } from 'src/consumer/constants'
import { platformWeb } from 'src/consumer/services'

dayjs.extend(utc)

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    maxWidth: 520,
    width: '100%',
  },
}))

const InfoBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: alpha('#6779e3', 0.03),
  border: '1px solid',
  borderColor: alpha('#6779e3', 0.1),
  borderRadius: theme.spacing(1.5),
}))

const InfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 4,
  '&:last-child': {
    marginBottom: 0,
  },
})

const SlotButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'isBooked',
})(({ theme, selected, isBooked }) => ({
  minWidth: 85,
  padding: theme.spacing(0.75, 1.25),
  borderRadius: theme.spacing(1),
  fontSize: '12px',
  fontWeight: 500,
  border: '1px solid',
  borderColor: isBooked ? '#fecaca' : selected ? '#6779e3' : '#e2e8f0',
  backgroundColor: isBooked ? '#fef2f2' : selected ? alpha('#6779e3', 0.1) : 'transparent',
  color: isBooked ? '#dc2626' : selected ? '#6779e3' : '#64748b',
  textDecoration: isBooked ? 'line-through' : 'none',
  '&:hover': {
    borderColor: isBooked ? '#fecaca' : '#6779e3',
    backgroundColor: isBooked ? '#fef2f2' : alpha('#6779e3', 0.05),
  },
  '&.Mui-disabled': {
    borderColor: '#f1f5f9',
    color: '#cbd5e1',
    backgroundColor: '#f8fafc',
  },
}))

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 600,
  color: '#374151',
  marginBottom: theme.spacing(1),
}))

// Default session duration in minutes
const DEFAULT_SESSION_DURATION = 30

/**
 * RebookingDialog - Complete booking dialog for rebooking past sessions
 * 
 * Pre-fills all fields from the existing booking (patient, practitioner, clinic, event type)
 * User only needs to select new date and time slot
 */
export default function RebookingDialog({
  open,
  onClose,
  patient,
  visitData,
  onSuccess,
}) {
  const [selectedDate, setSelectedDate] = useState(dayjs())
  const [bookedSlots, setBookedSlots] = useState([])
  const [slotsLoading, setSlotsLoading] = useState(false)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [useCustomTime, setUseCustomTime] = useState(false)
  const [customStartTime, setCustomStartTime] = useState(null)
  const [customEndTime, setCustomEndTime] = useState(null)
  const [bookingInProgress, setBookingInProgress] = useState(false)
  const [conflictWarning, setConflictWarning] = useState(null)
  const [creditsContext, setCreditsContext] = useState(null)
  const [creditsContextLoading, setCreditsContextLoading] = useState(false)

  // Fee fields - expanded by default
  const [collectPayment, setCollectPayment] = useState(true)
  const [feePaid, setFeePaid] = useState('')
  const [feePaymentMode, setFeePaymentMode] = useState('cash')
  const [feePaidTxnId, setFeePaidTxnId] = useState('')
  const [feeCollectionNote, setFeeCollectionNote] = useState('')
  const [feeError, setFeeError] = useState('')

  const eventType = visitData?.eventType
  const eventTypeName = displayNamesOfEventTypes[eventType] || eventType || 'Session'
  const mentorId = visitData?.mentorId
  const clinicId = visitData?.clinicId

  // Previous session fee info
  const previousFee = visitData?.feePaid || 0
  const previousCurrency = visitData?.currency || 'inr'
  const currencySymbol = previousCurrency === 'inr' ? '₹' : '$'

  // Payment mode options
  const paymentModeOptions = [
    { value: 'cash', label: 'Cash' },
    { value: 'upi', label: 'UPI' },
    { value: 'card', label: 'Card' },
    { value: 'netbanking', label: 'Net Banking' },
    { value: 'other', label: 'Other' },
  ]
  const clinicBrandId = visitData?.clinicBrandId

  // Get session duration from event data or use default
  const sessionDuration = visitData?.durationInMins || visitData?.duration || DEFAULT_SESSION_DURATION

  // Generate time slots for the day (every 30 mins from 8 AM to 8 PM)
  const timeSlots = useMemo(() => {
    const slots = []
    const startHour = 8 // 8 AM
    const endHour = 20 // 8 PM
    
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const slotStart = selectedDate.hour(hour).minute(minute).second(0)
        const slotEnd = slotStart.add(sessionDuration, 'minute')
        slots.push({
          startTime: slotStart,
          endTime: slotEnd,
          label: slotStart.format('h:mm A'),
        })
      }
    }
    return slots
  }, [selectedDate, sessionDuration])

  // Check if a time slot conflicts with booked slots
  const isSlotBooked = useCallback((slotStart, slotEnd) => {
    return bookedSlots.some(booked => {
      const bookedStart = dayjs.utc(booked.startTime).local()
      const bookedEnd = dayjs.utc(booked.endTime).local()
      // Check for overlap
      return slotStart.isBefore(bookedEnd) && slotEnd.isAfter(bookedStart)
    })
  }, [bookedSlots])

  useEffect(() => {
    if (!open || !patient?.id || !visitData?.subscriptionPlanId) {
      setCreditsContext(null)
      return
    }

    let active = true

    const fetchCreditsContext = async () => {
      setCreditsContextLoading(true)
      const response = await getSPDataWithParams(
        '/api/secure/cc/support/therapist/meeting/booking-credits-context',
        {
          patientId: patient.id,
          subscriptionPlanId: visitData.subscriptionPlanId,
          eventTypeId: visitData?.eventTypeId || '',
          eventType: visitData?.eventType || '',
          mentorId: visitData?.mentorId || '',
          clinicId: visitData?.clinicId || '',
          clinicBrandId: visitData?.clinicBrandId || '',
        },
      )

      if (!active) {
        return
      }

      if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        setCreditsContext(getDataMapFromCommonResponse(response)?.creditsContext || null)
      } else {
        setCreditsContext(null)
      }
      setCreditsContextLoading(false)
    }

    fetchCreditsContext()

    return () => {
      active = false
    }
  }, [
    open,
    patient?.id,
    visitData?.subscriptionPlanId,
    visitData?.eventTypeId,
    visitData?.eventType,
    visitData?.mentorId,
    visitData?.clinicId,
    visitData?.clinicBrandId,
  ])

  // Get conflicting booking details
  const getConflictingBooking = useCallback((slotStart, slotEnd) => {
    return bookedSlots.find(booked => {
      const bookedStart = dayjs.utc(booked.startTime).local()
      const bookedEnd = dayjs.utc(booked.endTime).local()
      return slotStart.isBefore(bookedEnd) && slotEnd.isAfter(bookedStart)
    })
  }, [bookedSlots])

  // Fetch booked slots when date or mentor changes
  useEffect(() => {
    if (!open || !mentorId || !selectedDate) return

    const fetchBookedSlots = async () => {
      setSlotsLoading(true)
      try {
        // Use ISO format with T separator as expected by the API
        const startOfDay = selectedDate.startOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
        const endOfDay = selectedDate.endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]')

        const response = await getSPDataWithParams(
          '/api/secure/cc/support/events/booked-slots/all-with-patient-info',
          {
            mentorId,
            startTime: startOfDay,
            endTime: endOfDay,
          }
        )

        if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
          const data = getDataMapFromCommonResponse(response)
          const slots = data?.bookedEvents || data?.bookedSlots || data || []
          // Filter only confirmed/booked slots (not cancelled)
          const activeSlots = Array.isArray(slots) 
            ? slots.filter(s => s.status === 'BOOKED' || !s.status)
            : []
          setBookedSlots(activeSlots)
        } else {
          setBookedSlots([])
        }
      } catch (error) {
        console.error('Error fetching booked slots:', error)
        setBookedSlots([])
      } finally {
        setSlotsLoading(false)
      }
    }

    fetchBookedSlots()
  }, [open, mentorId, selectedDate])

  // Check for conflicts when custom time changes
  useEffect(() => {
    if (!useCustomTime || !customStartTime || !customEndTime) {
      setConflictWarning(null)
      return
    }

    const startDateTime = selectedDate
      .hour(customStartTime.hour())
      .minute(customStartTime.minute())
      .second(0)
    const endDateTime = selectedDate
      .hour(customEndTime.hour())
      .minute(customEndTime.minute())
      .second(0)

    const conflict = getConflictingBooking(startDateTime, endDateTime)
    if (conflict) {
      const conflictStart = dayjs.utc(conflict.startTime).local().format('h:mm A')
      const conflictEnd = dayjs.utc(conflict.endTime).local().format('h:mm A')
      setConflictWarning(`Conflicts with existing booking: ${conflictStart} - ${conflictEnd}`)
    } else {
      setConflictWarning(null)
    }
  }, [useCustomTime, customStartTime, customEndTime, selectedDate, getConflictingBooking])

  // Fee validation function
  const validateFee = (value) => {
    if (!collectPayment) {
      setFeeError('')
      return true
    }
    if (!value || value === '') {
      setFeeError('Fee amount is required')
      return false
    }
    const numValue = parseFloat(value)
    if (isNaN(numValue) || numValue < 0) {
      setFeeError('Fee must be a valid positive number')
      return false
    }
    if (numValue === 0) {
      setFeeError('Fee amount cannot be zero')
      return false
    }
    setFeeError('')
    return true
  }

  // Handle fee change with validation
  const handleFeeChange = (e) => {
    const value = e.target.value
    setFeePaid(value)
    if (value) {
      validateFee(value)
    } else if (collectPayment) {
      setFeeError('Fee amount is required')
    }
  }

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedDate(dayjs())
      setSelectedSlot(null)
      setUseCustomTime(false)
      setCustomStartTime(null)
      setCustomEndTime(null)
      setConflictWarning(null)
      // Reset fee fields - keep expanded by default
      setCollectPayment(true)
      setFeePaid('')
      setFeePaymentMode('cash')
      setFeePaidTxnId('')
      setFeeCollectionNote('')
      setFeeError('')
    }
  }, [open])

  // Handle booking
  const handleBook = async ({
    paymentGatewayOverride = null,
    bundlePackPurchaseId = '',
  } = {}) => {
    // Validate fee if collecting payment
    const isCreditsBooking = paymentGatewayOverride === 'credits'
    if (!isCreditsBooking && collectPayment && !validateFee(feePaid)) {
      toast.error('Please enter a valid fee amount')
      return
    }

    let startTime, endTime

    if (useCustomTime) {
      if (!customStartTime || !customEndTime) {
        toast.error('Please select start and end time')
        return
      }
      startTime = selectedDate
        .hour(customStartTime.hour())
        .minute(customStartTime.minute())
        .second(0)
      endTime = selectedDate
        .hour(customEndTime.hour())
        .minute(customEndTime.minute())
        .second(0)
    } else {
      if (!selectedSlot) {
        toast.error('Please select a time slot')
        return
      }
      startTime = selectedSlot.startTime
      endTime = selectedSlot.endTime
    }

    // Double check for conflicts
    if (isSlotBooked(startTime, endTime) && !useCustomTime) {
      toast.error('This slot is already booked')
      return
    }

    setBookingInProgress(true)
    try {
      const clientTransactionId = uuidv4()

      const dataToSubmit = {
        apiParam: 'booked-event-info',
        mentorId: mentorId,
        userId: '',
        startTime: startTime.utc().format('YYYY-MM-DD HH:mm:ss'),
        endTime: endTime.utc().format('YYYY-MM-DD HH:mm:ss'),
        eventType: eventType,
        eventTypeId: visitData?.eventTypeId || '',
        subscriptionPlanId: visitData?.subscriptionPlanId || '',
        patientId: patient?.id,
        id: '',
        feePaid: isCreditsBooking ? '' : collectPayment ? (parseFloat(feePaid) || 0) : '',
        currency: previousCurrency || 'inr',
        feePaymentMode: isCreditsBooking ? '' : collectPayment ? feePaymentMode : '',
        feePaidTxnId: isCreditsBooking ? '' : collectPayment ? feePaidTxnId : '',
        feeCollectionNote: isCreditsBooking ? '' : feeCollectionNote || '',
        clinicId: clinicId || '',
        clinicBrandId: clinicBrandId || '',
        bookingTime: dayjs().format(MOMENT_FORMATS.javaInstantFormat),
        screeningRequest: {
          requested: false,
          reasonId: '',
          reason: '',
        },
        // Reference to original booking for tracking
        rebookedFromBookingId: visitData?.id,
        ...(bundlePackPurchaseId ? { bundlePackPurchaseId } : {}),
      }

      // Determine payment gateway based on collectPayment
      const paymentGateway = paymentGatewayOverride || (collectPayment ? 'paidOffline' : 'free')

      const response = await putSPDataWithParams(
        '/api/secure/cc/support/therapist/meeting/book',
        dataToSubmit,
        {
          teacherId: mentorId,
          paymentGateway: paymentGateway,
          clientTransactionId: clientTransactionId,
          platform: platformWeb,
          paymentMethod: feePaymentMode || 'upi',
          sendPaymentLinkToPatient: false,
          sendPaymentLinkToPatientRelative: false,
          phoneNumbersToSendThePaymentLink: '',
          cancelUrl: encodeURIComponent(window.location.href),
          successUrl: encodeURIComponent(window.location.href),
        }
      )

      if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        toast.success('Session rebooked successfully')
        onSuccess?.()
        onClose()
      } else {
        toast.error(response?.data?.message || 'Failed to create booking')
      }
    } catch (error) {
      console.error('Error creating booking:', error)
      toast.error('Failed to create booking')
    } finally {
      setBookingInProgress(false)
    }
  }

  // Get event type color
  const getEventTypeColor = () => {
    switch (eventType) {
      case allAvailableEventTypes.therapySession:
        return '#10b981'
      case allAvailableEventTypes.consultation:
        return '#6366f1'
      case allAvailableEventTypes.screening:
        return '#f59e0b'
      default:
        return '#6779e3'
    }
  }

  const eventTypeColor = getEventTypeColor()

  // Check if time slot is selected
  const hasTimeSlot = useCustomTime 
    ? (customStartTime && customEndTime) 
    : selectedSlot !== null

  // Check if fee is valid when collecting payment
  const isFeeValid = collectPayment 
    ? (feePaid && parseFloat(feePaid) > 0 && feePaymentMode && !feeError)
    : true

  // Check if we can book (time + fee validation)
  const canBook = hasTimeSlot && isFeeValid
  const canUseCredits = creditsContext?.eligible === true
  const canBookWithCredits = hasTimeSlot

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                backgroundColor: alpha(eventTypeColor, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <EventRepeatIcon sx={{ color: eventTypeColor, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: '18px' }}>
                Rebook {eventTypeName}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5 }}>
        {/* Event Type Badge - Prominent with Duration */}
        <Box sx={{ 
          mb: 2, 
          p: 1.5, 
          borderRadius: 2,
          background: `linear-gradient(135deg, ${alpha(eventTypeColor, 0.1)} 0%, ${alpha(eventTypeColor, 0.05)} 100%)`,
          border: `1px solid ${alpha(eventTypeColor, 0.2)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventIcon sx={{ color: eventTypeColor, fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: eventTypeColor }}>
              {eventTypeName}
            </Typography>
            <Chip 
              label="Rebooking" 
              size="small" 
              sx={{ 
                height: 20, 
                fontSize: '10px', 
                fontWeight: 600,
                backgroundColor: alpha(eventTypeColor, 0.15),
                color: eventTypeColor,
              }} 
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TimerIcon sx={{ fontSize: 18, color: '#6779e3' }} />
            <Typography variant="body2" fontWeight={600} color="#6779e3">
              {sessionDuration} mins
            </Typography>
          </Box>
        </Box>

        {/* Pre-filled Booking Info (Read-only) */}
        <InfoBox sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {/* Patient */}
            <Box sx={{ flex: '1 1 45%', minWidth: 150 }}>
              <InfoRow>
                <PersonIcon sx={{ fontSize: 16, color: eventTypeColor }} />
                <Typography variant="body2" fontWeight={500}>
                  {patient?.name || 'Patient'}
                </Typography>
              </InfoRow>
              {patient?.mobile && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                  {patient.mobile}
                </Typography>
              )}
            </Box>

            {/* Practitioner */}
            <Box sx={{ flex: '1 1 45%', minWidth: 150 }}>
              <InfoRow>
                <PersonPinIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2">
                  {visitData?.mentorName || 'Practitioner'}
                </Typography>
              </InfoRow>
            </Box>

            {/* Clinic */}
            {(visitData?.clinicBrandData?.brandName || visitData?.clinicLocation?.title || visitData?.clinicName) && (
              <Box sx={{ flex: '1 1 100%' }}>
                <InfoRow>
                  <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    {visitData?.clinicBrandData?.brandName || visitData?.clinicName}
                    {visitData?.clinicLocation?.title && ` - ${visitData.clinicLocation.title}`}
                  </Typography>
                </InfoRow>
              </Box>
            )}
          </Box>
        </InfoBox>

        {/* Previous Session Fee Info */}
        {previousFee > 0 && (
          <InfoBox sx={{ mb: 2, backgroundColor: alpha('#f59e0b', 0.05), borderColor: alpha('#f59e0b', 0.15) }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <ReceiptIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
              <Typography variant="subtitle2" fontWeight={600} color="#92400e">
                Previous Session Fee
              </Typography>
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#b45309', ml: 3 }}>
              {currencySymbol}{previousFee}
            </Typography>
            {visitData?.feePaymentMode && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                Paid via {visitData.feePaymentMode}
              </Typography>
            )}
          </InfoBox>
        )}

        {/* Date Selection */}
        <Box sx={{ mb: 2 }}>
          <SectionLabel>Select Date</SectionLabel>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={selectedDate}
              onChange={(newDate) => {
                setSelectedDate(newDate)
                setSelectedSlot(null)
              }}
              minDate={dayjs()}
              sx={{ width: '100%' }}
              slotProps={{
                textField: { size: 'small' },
              }}
            />
          </LocalizationProvider>
        </Box>

        {/* Time Slots */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <SectionLabel sx={{ mb: 0 }}>Select Time Slot</SectionLabel>
            <Button
              size="small"
              variant={useCustomTime ? 'contained' : 'outlined'}
              onClick={() => {
                setUseCustomTime(!useCustomTime)
                setSelectedSlot(null)
              }}
              sx={{ 
                fontSize: '11px', 
                py: 0.25, 
                px: 1,
                backgroundColor: useCustomTime ? eventTypeColor : 'transparent',
                borderColor: eventTypeColor,
                color: useCustomTime ? 'white' : eventTypeColor,
                '&:hover': {
                  backgroundColor: useCustomTime ? alpha(eventTypeColor, 0.85) : alpha(eventTypeColor, 0.1),
                  borderColor: eventTypeColor,
                },
              }}
            >
              Custom Time
            </Button>
          </Box>

          {slotsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} sx={{ color: eventTypeColor }} />
            </Box>
          ) : useCustomTime ? (
            <Box>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                  <TimePicker
                    label="Start Time"
                    value={customStartTime}
                    onChange={(newTime) => {
                      setCustomStartTime(newTime)
                      // Auto-set end time based on session duration
                      if (newTime) {
                        setCustomEndTime(newTime.add(sessionDuration, 'minute'))
                      }
                    }}
                    sx={{ flex: 1 }}
                    slotProps={{
                      textField: { size: 'small' },
                    }}
                  />
                  <TimePicker
                    label="End Time"
                    value={customEndTime}
                    onChange={setCustomEndTime}
                    sx={{ flex: 1 }}
                    slotProps={{
                      textField: { size: 'small' },
                    }}
                  />
                </Box>
              </LocalizationProvider>
              
              {conflictWarning && (
                <Alert 
                  severity="warning" 
                  icon={<WarningAmberIcon sx={{ fontSize: 20 }} />}
                  sx={{ py: 0.5, fontSize: '12px' }}
                >
                  {conflictWarning}
                </Alert>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, maxHeight: 200, overflow: 'auto' }}>
              {timeSlots.map((slot, idx) => {
                const booked = isSlotBooked(slot.startTime, slot.endTime)
                const isSelected = selectedSlot?.label === slot.label
                const conflictingSlot = booked ? getConflictingBooking(slot.startTime, slot.endTime) : null
                
                return (
                  <Tooltip
                    key={idx}
                    title={
                      booked && conflictingSlot
                        ? `Booked: ${dayjs.utc(conflictingSlot.startTime).local().format('h:mm A')} - ${dayjs.utc(conflictingSlot.endTime).local().format('h:mm A')}`
                        : ''
                    }
                    arrow
                  >
                    <span>
                      <SlotButton
                        selected={isSelected}
                        isBooked={booked}
                        onClick={() => !booked && setSelectedSlot(slot)}
                        disabled={booked}
                      >
                        {slot.label}
                      </SlotButton>
                    </span>
                  </Tooltip>
                )
              })}
            </Box>
          )}
        </Box>

        {/* Fee Collection Section - Expanded by default */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <SectionLabel sx={{ mb: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PaymentIcon sx={{ fontSize: 18, color: '#6779e3' }} />
              Fee Collection
            </SectionLabel>
            <Chip
              label={collectPayment ? 'Collecting Payment' : 'Free / Pay Later'}
              size="small"
              onClick={() => setCollectPayment(!collectPayment)}
              sx={{
                height: 24,
                fontSize: '11px',
                cursor: 'pointer',
                backgroundColor: collectPayment ? alpha('#10b981', 0.15) : alpha('#64748b', 0.1),
                color: collectPayment ? '#10b981' : '#64748b',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: collectPayment ? alpha('#10b981', 0.25) : alpha('#64748b', 0.2),
                },
              }}
            />
          </Box>

          <Box sx={{ 
            p: 1.5, 
            borderRadius: 1.5, 
            border: `1px solid ${collectPayment ? '#e2e8f0' : '#f1f5f9'}`,
            backgroundColor: collectPayment ? '#fafafa' : '#f8fafc',
          }}>
            {collectPayment ? (
              <>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
                  {/* Fee Amount - Required */}
                  <TextField
                    label="Fee Amount *"
                    type="number"
                    size="small"
                    value={feePaid}
                    onChange={handleFeeChange}
                    onBlur={() => validateFee(feePaid)}
                    error={!!feeError}
                    helperText={feeError}
                    placeholder={previousFee > 0 ? `Previous: ${currencySymbol}${previousFee}` : 'Enter amount'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CurrencyRupeeIcon sx={{ fontSize: 16, color: feeError ? 'error.main' : 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ flex: '1 1 120px', minWidth: 120 }}
                  />

                  {/* Payment Mode */}
                  <FormControl size="small" sx={{ flex: '1 1 130px', minWidth: 130 }}>
                    <InputLabel>Payment Mode</InputLabel>
                    <Select
                      value={feePaymentMode}
                      label="Payment Mode"
                      onChange={(e) => setFeePaymentMode(e.target.value)}
                    >
                      {paymentModeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Transaction ID (for non-cash) */}
                  {feePaymentMode !== 'cash' && (
                    <TextField
                      label="Transaction ID"
                      size="small"
                      value={feePaidTxnId}
                      onChange={(e) => setFeePaidTxnId(e.target.value)}
                      placeholder="Optional"
                      sx={{ flex: '1 1 150px', minWidth: 150 }}
                    />
                  )}
                </Box>

                {/* Quick fill from previous fee */}
                {previousFee > 0 && !feePaid && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setFeePaid(String(previousFee))}
                    sx={{ mb: 1.5, fontSize: '11px', color: '#f59e0b' }}
                  >
                    Use previous fee: {currencySymbol}{previousFee}
                  </Button>
                )}

                {/* Note field */}
                <TextField
                  label="Note"
                  size="small"
                  value={feeCollectionNote}
                  onChange={(e) => setFeeCollectionNote(e.target.value)}
                  placeholder="Add a note for this booking (optional)"
                  multiline
                  rows={2}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                        <NotesIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  No payment will be collected. You can add a note below.
                </Typography>
                {/* Note field for free/later */}
                <TextField
                  label="Note"
                  size="small"
                  value={feeCollectionNote}
                  onChange={(e) => setFeeCollectionNote(e.target.value)}
                  placeholder="Add a note for this booking (optional)"
                  multiline
                  rows={2}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                        <NotesIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Booking Summary */}
        {canBook && (
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 1.5, 
            backgroundColor: alpha('#10b981', 0.05),
            border: `1px solid ${alpha('#10b981', 0.2)}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />
              <Typography variant="subtitle2" fontWeight={600} color="#10b981">
                Booking Summary
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3, flexWrap: 'wrap' }}>
              <EventIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="body2">
                {selectedDate.format('ddd, DD MMM YYYY')}
              </Typography>
              <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary', ml: 1 }} />
              <Typography variant="body2">
                {useCustomTime
                  ? `${customStartTime?.format('h:mm A')} - ${customEndTime?.format('h:mm A')}`
                  : `${selectedSlot?.startTime.format('h:mm A')} - ${selectedSlot?.endTime.format('h:mm A')}`
                }
              </Typography>
              <Chip 
                icon={<TimerIcon sx={{ fontSize: 12 }} />}
                label={`${sessionDuration} mins`}
                size="small"
                sx={{ height: 20, fontSize: '10px', backgroundColor: alpha('#6779e3', 0.1), color: '#6779e3', '& .MuiChip-icon': { color: 'inherit' } }}
              />
              {collectPayment && feePaid && (
                <>
                  <PaymentIcon sx={{ fontSize: 14, color: 'text.secondary', ml: 1 }} />
                  <Typography variant="body2" fontWeight={600}>
                    {currencySymbol}{feePaid} ({feePaymentMode})
                  </Typography>
                </>
              )}
              {!collectPayment && (
                <Chip 
                  label="Free/Pay Later" 
                  size="small" 
                  sx={{ height: 18, fontSize: '10px', ml: 1, backgroundColor: alpha('#64748b', 0.1) }} 
                />
              )}
            </Box>
          </Box>
        )}
        {canUseCredits && creditsContext?.summary ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            {creditsContext.summary}
          </Alert>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={bookingInProgress}>
          Cancel
        </Button>
        {canUseCredits ? (
          <Button
            variant="outlined"
            color="success"
            onClick={() =>
              handleBook({
                paymentGatewayOverride: 'credits',
                bundlePackPurchaseId: creditsContext?.recommendedBundlePackPurchaseId || '',
              })
            }
            disabled={!canBookWithCredits || bookingInProgress || creditsContextLoading || (useCustomTime && conflictWarning)}
          >
            {creditsContextLoading ? 'Checking credits...' : 'Use Pack / Credits'}
          </Button>
        ) : null}
        <Button
          variant="contained"
          onClick={() => handleBook()}
          disabled={!canBook || bookingInProgress || (useCustomTime && conflictWarning)}
          startIcon={bookingInProgress && <CircularProgress size={16} color="inherit" />}
          sx={{
            backgroundColor: eventTypeColor,
            '&:hover': { backgroundColor: alpha(eventTypeColor, 0.85) },
          }}
        >
          {bookingInProgress ? 'Booking...' : 'Rebook Session'}
        </Button>
      </DialogActions>
    </StyledDialog>
  )
}
